// Design B building blocks shared by the Teaching pages: KPI cards, bar
// cells for rates, plain-language chips and a table card. Indigo stays for
// navigation and actions; rates use the SOP §41 red to green scale.

import type { ComponentChildren } from 'preact';
import { INK, pct, perfFill } from './ui';

export { TeachingPage as PageHeader, Card } from './ui';

/** Bar colour on the §41 scale, a step deeper than the cell fill so a thin bar reads on white. */
export function perfBar(rate: number): string {
  const stops: [number, [number, number, number]][] = [[0, [236, 150, 150]], [0.5, [240, 196, 120]], [1, [134, 204, 150]]];
  const x = Math.max(0, Math.min(1, rate));
  const [a, b] = x <= 0.5 ? [stops[0], stops[1]] : [stops[1], stops[2]];
  const t = (x - a[0]) / (b[0] - a[0]);
  const c = a[1].map((v, i) => Math.round(v + (b[1][i] - v) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

export function KpiRow({ children, cols = 4 }: { children: ComponentChildren; cols?: 3 | 4 | 5 }) {
  const lg = { 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' }[cols];
  return <div class={'grid grid-cols-2 gap-3 ' + lg} data-kpi-row>{children}</div>;
}

/** Headline number + a one-line plain label. `tone` colours the value only. */
export function KpiCard({ value, label, hint, tone, side }: { value: ComponentChildren; label: string; hint?: ComponentChildren; tone?: string; side?: ComponentChildren }) {
  return (
    <div class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 md:px-5 py-4 flex items-center gap-3 min-w-0" data-kpi={label}>
      <div class="min-w-0 flex-1">
        <div class="text-[24px] md:text-[30px] font-semibold tracking-[-0.03em] leading-none tabular-nums text-[var(--color-text)] truncate" style={tone ? { color: tone } : undefined}>{value}</div>
        <div class="text-[12.5px] text-[var(--color-text-muted)] mt-1.5">{label}</div>
        {hint && <div class="text-[11.5px] text-[var(--color-text-faint)] mt-0.5 line-clamp-2" title={typeof hint === 'string' ? hint : undefined}>{hint}</div>}
      </div>
      {side && <div class="shrink-0">{side}</div>}
    </div>
  );
}

/** Rate as a bar with the % beside it. null is "no evidence", never a zero. */
export function BarCell({ rate, label, min = 96 }: { rate: number | null; label?: string; min?: number }) {
  if (rate === null) return <div class="text-[11.5px] text-[var(--color-text-faint)] whitespace-nowrap" title="no evidence">{label ?? 'no evidence'}</div>;
  return (
    <div class="flex items-center gap-2" style={{ minWidth: `${min}px` }}>
      <div class="flex-1 h-2 rounded-full bg-[var(--color-elevated)] overflow-hidden">
        <div class="h-full rounded-full" style={{ width: `${Math.max(rate * 100, 2)}%`, background: perfBar(rate) }} />
      </div>
      <span class="w-[38px] text-right tabular-nums text-[12px] font-medium text-[var(--color-text)]">{label ?? pct(rate)}</span>
    </div>
  );
}

export type ChipTone = 'strong' | 'reteach' | 'watch' | 'neutral';
const CHIP_BG: Record<ChipTone, string> = { strong: '#d3f0d9', reteach: '#f8d4d4', watch: '#fde9b8', neutral: '#e8eaee' };

export function Chip({ tone, children, title }: { tone: ChipTone; children: ComponentChildren; title?: string }) {
  return (
    <span title={title} class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap" style={{ background: CHIP_BG[tone], color: INK }}>
      {children}
    </span>
  );
}

/** Table inside a white card; scrolls sideways on phone. */
export function TableCard({ children, class: cls }: { children: ComponentChildren; class?: string }) {
  return <div class={'rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-x-auto ' + (cls ?? '')}>{children}</div>;
}

export const thCls = 'px-3 py-2.5 text-[11.5px] font-medium text-[var(--color-text-muted)] text-left whitespace-nowrap bg-[#fafbfc] border-b border-[var(--color-border)]';
export const tdCls = 'px-3 py-2 text-[12.5px] text-[var(--color-text)]';

export { perfFill };
